import React, { useState, useEffect } from 'react';
import { useChat } from '../../context/ChatContext';
import { X, Save, Check, XCircle, MessageCircle, Users, Edit, Trash2, ThumbsUp, ThumbsDown } from 'lucide-react';

interface ChatRoleSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChatRoleSettingsModal: React.FC<ChatRoleSettingsModalProps> = ({ isOpen, onClose }) => {
  const { roleSettings, updateRoleSettings } = useChat();
  const [localSettings, setLocalSettings] = useState(roleSettings);
  const [isLoading, setIsLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    setLocalSettings(roleSettings);
  }, [roleSettings]);

  const handleSettingChange = (roleName: string, setting: string, value: boolean) => {
    setLocalSettings(prev => 
      prev.map(role => 
        role.role_name === roleName 
          ? { ...role, [setting]: value }
          : role
      )
    );
  };

  const handleSave = async () => {
    setIsLoading(true);
    setSaveMessage('');
    
    try {
      await updateRoleSettings(localSettings);
      setSaveMessage('Configuración guardada exitosamente');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      setSaveMessage('Error al guardar la configuración');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const getPermissionIcon = (permission: string) => {
    const icons = {
      can_access_chat_page: <MessageCircle className="h-5 w-5 text-blue-600" />,
      can_create_conversations: <MessageCircle className="h-5 w-5 text-green-600" />,
      can_join_conversations: <Users className="h-5 w-5 text-purple-600" />,
      can_send_messages: <MessageCircle className="h-5 w-5 text-blue-600" />,
      can_edit_messages: <Edit className="h-5 w-5 text-yellow-600" />,
      can_delete_messages: <Trash2 className="h-5 w-5 text-red-600" />,
      can_react_to_messages: <ThumbsUp className="h-5 w-5 text-green-600" />
    };
    return icons[permission as keyof typeof icons] || <MessageCircle className="h-5 w-5 text-gray-600" />;
  };

  const getPermissionLabel = (permission: string) => {
    const labels = {
      can_access_chat_page: 'Acceder a la página de chat',
      can_create_conversations: 'Crear conversaciones',
      can_join_conversations: 'Unirse a conversaciones',
      can_send_messages: 'Enviar mensajes',
      can_edit_messages: 'Editar mensajes',
      can_delete_messages: 'Eliminar mensajes',
      can_react_to_messages: 'Reaccionar a mensajes'
    };
    return labels[permission as keyof typeof labels] || permission;
  };

  const getPermissionDescription = (permission: string) => {
    const descriptions = {
      can_access_chat_page: 'Permite ver la página principal del chat',
      can_create_conversations: 'Permite crear nuevas conversaciones o pestañas',
      can_join_conversations: 'Permite unirse a conversaciones existentes',
      can_send_messages: 'Permite escribir y enviar mensajes',
      can_edit_messages: 'Permite editar mensajes propios',
      can_delete_messages: 'Permite eliminar mensajes propios',
      can_react_to_messages: 'Permite dar like/dislike a mensajes'
    };
    return descriptions[permission as keyof typeof descriptions] || '';
  };

  const getToggleClasses = (isDisabled: boolean, isChecked: boolean) => {
    if (isDisabled) {
      return 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed';
    }
    
    if (isChecked) {
      return 'bg-blue-600 peer-checked:bg-blue-600';
    }
    
    return 'bg-gray-200 dark:bg-gray-700';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <MessageCircle className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Configuración de Roles del Chat
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="space-y-6">
            {localSettings.map((roleSetting) => (
              <div key={roleSetting.role_name} className="border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    {roleSetting.role_name}
                  </h3>
                </div>
                
                <div className="p-4 space-y-4">
                  {/* Acceder a la página de chat */}
                  <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-3">
                      {getPermissionIcon('can_access_chat_page')}
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {getPermissionLabel('can_access_chat_page')}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {getPermissionDescription('can_access_chat_page')}
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={roleSetting.can_access_chat_page}
                        onChange={(e) => handleSettingChange(roleSetting.role_name, 'can_access_chat_page', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className={`w-11 h-6 rounded-full peer transition-colors ${getToggleClasses(false, roleSetting.can_access_chat_page)}`}>
                        <div className={`w-5 h-5 bg-white rounded-full transition-transform transform ${roleSetting.can_access_chat_page ? 'translate-x-5' : 'translate-x-0'} mt-0.5 ml-0.5`} />
                      </div>
                    </label>
                  </div>

                  {/* Crear conversaciones */}
                  <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-3">
                      {getPermissionIcon('can_create_conversations')}
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {getPermissionLabel('can_create_conversations')}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {getPermissionDescription('can_create_conversations')}
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={roleSetting.can_create_conversations}
                        onChange={(e) => handleSettingChange(roleSetting.role_name, 'can_create_conversations', e.target.checked)}
                        className="sr-only peer"
                        disabled={!roleSetting.can_access_chat_page}
                      />
                      <div className={`w-11 h-6 rounded-full peer transition-colors ${getToggleClasses(!roleSetting.can_access_chat_page, roleSetting.can_create_conversations)}`}>
                        <div className={`w-5 h-5 bg-white rounded-full transition-transform transform ${roleSetting.can_create_conversations ? 'translate-x-5' : 'translate-x-0'} mt-0.5 ml-0.5`} />
                      </div>
                    </label>
                  </div>

                  {/* Unirse a conversaciones */}
                  <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-3">
                      {getPermissionIcon('can_join_conversations')}
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {getPermissionLabel('can_join_conversations')}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {getPermissionDescription('can_join_conversations')}
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={roleSetting.can_join_conversations}
                        onChange={(e) => handleSettingChange(roleSetting.role_name, 'can_join_conversations', e.target.checked)}
                        className="sr-only peer"
                        disabled={!roleSetting.can_access_chat_page}
                      />
                      <div className={`w-11 h-6 rounded-full peer transition-colors ${getToggleClasses(!roleSetting.can_access_chat_page, roleSetting.can_join_conversations)}`}>
                        <div className={`w-5 h-5 bg-white rounded-full transition-transform transform ${roleSetting.can_join_conversations ? 'translate-x-5' : 'translate-x-0'} mt-0.5 ml-0.5`} />
                      </div>
                    </label>
                  </div>

                  {/* Enviar mensajes */}
                  <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-3">
                      {getPermissionIcon('can_send_messages')}
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {getPermissionLabel('can_send_messages')}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {getPermissionDescription('can_send_messages')}
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={roleSetting.can_send_messages}
                        onChange={(e) => handleSettingChange(roleSetting.role_name, 'can_send_messages', e.target.checked)}
                        className="sr-only peer"
                        disabled={!roleSetting.can_access_chat_page}
                      />
                      <div className={`w-11 h-6 rounded-full peer transition-colors ${getToggleClasses(!roleSetting.can_access_chat_page, roleSetting.can_send_messages)}`}>
                        <div className={`w-5 h-5 bg-white rounded-full transition-transform transform ${roleSetting.can_send_messages ? 'translate-x-5' : 'translate-x-0'} mt-0.5 ml-0.5`} />
                      </div>
                    </label>
                  </div>

                  {/* Editar mensajes */}
                  <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-3">
                      {getPermissionIcon('can_edit_messages')}
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {getPermissionLabel('can_edit_messages')}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {getPermissionDescription('can_edit_messages')}
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={roleSetting.can_edit_messages}
                        onChange={(e) => handleSettingChange(roleSetting.role_name, 'can_edit_messages', e.target.checked)}
                        className="sr-only peer"
                        disabled={!roleSetting.can_send_messages}
                      />
                      <div className={`w-11 h-6 rounded-full peer transition-colors ${getToggleClasses(!roleSetting.can_send_messages, roleSetting.can_edit_messages)}`}>
                        <div className={`w-5 h-5 bg-white rounded-full transition-transform transform ${roleSetting.can_edit_messages ? 'translate-x-5' : 'translate-x-0'} mt-0.5 ml-0.5`} />
                      </div>
                    </label>
                  </div>

                  {/* Eliminar mensajes */}
                  <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-3">
                      {getPermissionIcon('can_delete_messages')}
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {getPermissionLabel('can_delete_messages')}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {getPermissionDescription('can_delete_messages')}
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={roleSetting.can_delete_messages}
                        onChange={(e) => handleSettingChange(roleSetting.role_name, 'can_delete_messages', e.target.checked)}
                        className="sr-only peer"
                        disabled={!roleSetting.can_send_messages}
                      />
                      <div className={`w-11 h-6 rounded-full peer transition-colors ${getToggleClasses(!roleSetting.can_send_messages, roleSetting.can_delete_messages)}`}>
                        <div className={`w-5 h-5 bg-white rounded-full transition-transform transform ${roleSetting.can_delete_messages ? 'translate-x-5' : 'translate-x-0'} mt-0.5 ml-0.5`} />
                      </div>
                    </label>
                  </div>

                  {/* Reaccionar a mensajes */}
                  <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-3">
                      {getPermissionIcon('can_react_to_messages')}
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {getPermissionLabel('can_react_to_messages')}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {getPermissionDescription('can_react_to_messages')}
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={roleSetting.can_react_to_messages}
                        onChange={(e) => handleSettingChange(roleSetting.role_name, 'can_react_to_messages', e.target.checked)}
                        className="sr-only peer"
                        disabled={!roleSetting.can_access_chat_page}
                      />
                      <div className={`w-11 h-6 rounded-full peer transition-colors ${getToggleClasses(!roleSetting.can_access_chat_page, roleSetting.can_react_to_messages)}`}>
                        <div className={`w-5 h-5 bg-white rounded-full transition-transform transform ${roleSetting.can_react_to_messages ? 'translate-x-5' : 'translate-x-0'} mt-0.5 ml-0.5`} />
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            {saveMessage && (
              <div className={`flex items-center space-x-2 px-3 py-2 rounded-md ${
                saveMessage.includes('Error') 
                  ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' 
                  : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
              }`}>
                {saveMessage.includes('Error') ? (
                  <XCircle className="h-4 w-4" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                <span className="text-sm font-medium">{saveMessage}</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              <Save className="h-4 w-4" />
              <span>{isLoading ? 'Guardando...' : 'Guardar'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatRoleSettingsModal;
